import React, { useState } from 'react';
import { IconButton, Tooltip, Snackbar, Alert, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import RssFeedIcon from '@mui/icons-material/RssFeed';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * RSSButton - Button to subscribe to RSS feed for search results
 *
 * @param {string} collection - The collection type (hansard, bills, qon, etc.)
 * @param {string} query - The current search query
 * @param {boolean} disabled - Whether the button is disabled
 */
export default function RSSButton({ collection, query = '*', disabled = false }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [copied, setCopied] = useState(false);

  const feedUrl = `${API_URL}/api/feeds/${collection}?q=${encodeURIComponent(query)}`;

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(feedUrl);
      setCopied(true);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = feedUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
    }
    handleClose();
  };

  const handleOpenFeed = () => {
    window.open(feedUrl, '_blank');
    handleClose();
  };

  return (
    <>
      <Tooltip title="Subscribe to RSS Feed" arrow>
        <span>
          <IconButton
            onClick={handleClick}
            disabled={disabled}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                color: 'warning.main',
                backgroundColor: 'action.hover'
              }
            }}
          >
            <RssFeedIcon />
          </IconButton>
        </span>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleCopyUrl}>
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy Feed URL</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleOpenFeed}>
          <ListItemIcon>
            <OpenInNewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Open Feed</ListItemText>
        </MenuItem>
      </Menu>

      <Snackbar
        open={copied}
        autoHideDuration={3000}
        onClose={() => setCopied(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setCopied(false)}>
          RSS feed URL copied to clipboard
        </Alert>
      </Snackbar>
    </>
  );
}
